/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable react/no-array-index-key */
import React, { ReactElement, ReactNode, useCallback, useState } from 'react';

import omit from 'lodash-es/omit';
import { ArrowUpDownIcon, FilterIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useTable,
  useRowSelect,
  useGlobalFilter,
  useSortBy,
  Column as ReactTableColumn,
  UseTableColumnOptions,
  CellProps,
  useFlexLayout,
} from 'react-table';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { OptionBase } from '@/shared/types/common';
import { noop } from '@/util/util';

import { TableAPIFilter } from './TableAPIfilter';
import { TableCheckbox } from './TableCheckbox';
import { TableFilter } from './TableFilter';
import { TableSelection } from './TableSelection';
import DropdownList from '../DropdownList/DropdownList';

import styles from './Table.module.scss';

// Extend react-table types to include plugin properties
declare module 'react-table' {
  export interface TableInstance<D extends object = {}>
    extends
      UseGlobalFiltersInstanceProps<D>,
      UseSortByInstanceProps<D>,
      UseRowSelectInstanceProps<D> {}

  export interface TableState<D extends object = {}>
    extends UseGlobalFiltersState<D>, UseSortByState<D>, UseRowSelectState<D> {}

  export interface ColumnInstance<D extends object = {}>
    extends UseFiltersColumnProps<D>, UseSortByColumnProps<D> {}

  export interface Row<D extends object = {}> extends UseRowSelectRowProps<D> {}

  export interface TableOptions<D extends object>
    extends UseGlobalFiltersOptions<D>, UseSortByOptions<D>, UseRowSelectOptions<D> {}
}

interface Column<T extends object> extends UseTableColumnOptions<T> {
  Cell?: (props: CellProps<T>) => ReactElement | string | number | ReactNode;
}

type TableProps<T extends object> = {
  columns: Column<T>[];
  data: T[];
  selectable?: boolean;
  checkable?: boolean;
  filterable?: boolean;
  sortable?: boolean;
  emptyMessage?: string;
  overflown?: boolean;
  noGutters?: boolean;
  fullWidth?: boolean;
  filterableIcon?: boolean;
  searchable?: boolean;
  onSelectionClick?: (row) => void;
  sortBy?: string;
  filterOptions?: OptionBase[];
  onFilterChange?: (val: number | string) => void;
  flexLayout?: boolean;
  searchPlaceholder?: string;
  headerHeight?: 'small' | 'medium';
  hideHeader?: boolean;
  title?: string;
  onAPISearch?: (search: string) => void;
  loading?: boolean;
  defaultSearchValue?: string;
};

const Table = <T extends object>({
  columns,
  data,
  selectable = false,
  checkable = false,
  filterable = false,
  sortable,
  searchable = true,
  emptyMessage = '',
  filterableIcon = true,
  fullWidth = true,
  overflown,
  noGutters,
  onSelectionClick = noop,
  sortBy,
  filterOptions,
  onFilterChange,
  flexLayout = false,
  searchPlaceholder,
  headerHeight = 'small',
  hideHeader = false,
  title,
  loading,
  onAPISearch,
  defaultSearchValue,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);

  const handleOptionClick = useCallback(
    (item) => {
      const index = filterOptions.findIndex((f) => f.value === item.value);
      setSelected(index);
      onFilterChange(item.value);
    },
    [filterOptions, onFilterChange]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    toggleAllRowsSelected,
    toggleRowSelected,
  } = useTable<T>(
    {
      columns: columns as ReactTableColumn<T>[],
      data,
      disableSortRemove: true,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          { id: sortBy, desc: sortBy === 'last_used' || sortBy === 'time' || sortBy === 'updated' },
        ],
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => {
          let tempRow1 = row1.values[columnName];
          let tempRow2 = row2.values[columnName];

          if (typeof tempRow1 === 'string') {
            tempRow1 = tempRow1.toLowerCase();
          }
          if (typeof tempRow2 === 'string') {
            tempRow2 = tempRow2.toLowerCase();
          }

          return tempRow1 > tempRow2 ? 1 : -1;
        },
      },
    },
    useGlobalFilter,
    useSortBy,
    useRowSelect,
    flexLayout && useFlexLayout,
    (hooks) => {
      if (checkable) {
        hooks.visibleColumns.push((cols) => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <TableCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => <TableCheckbox {...row.getToggleRowSelectedProps()} />,
          },
          ...cols,
        ]);
      } else if (selectable) {
        hooks.visibleColumns.push((cols) => [
          {
            id: 'selection',
            Header: '',
            Cell: ({ row }) => (
              <TableSelection
                {...row.getToggleRowSelectedProps()}
                onClick={() => {
                  toggleAllRowsSelected(false);
                  toggleRowSelected(row.id, true);
                  onSelectionClick(row.original as T);
                }}
              />
            ),
          },
          ...cols,
        ]);
      }
    }
  );
  return (
    <>
      <div
        className={cn(styles.container, 'tableContainer', styles[`headerHeight--${headerHeight}`], {
          [styles.fullWidth]: fullWidth,
          [styles.overflown]: overflown,
          [styles.noGutters]: noGutters,
        })}
      >
        {title && (
          <h2 className="mb-6 text-base leading-6 font-bold text-muted-foreground">{title}</h2>
        )}
        <table {...getTableProps()}>
          {!hideHeader && (
            <thead>
              {filterable && (
                <tr>
                  <th colSpan={visibleColumns?.length} className={styles.filterRow}>
                    <div
                      className={cn(styles.filterContainer, { [styles.flexLayout]: flexLayout })}
                    >
                      {searchable && (
                        <TableFilter
                          preGlobalFilteredRows={preGlobalFilteredRows}
                          globalFilter={state.globalFilter}
                          setGlobalFilter={setGlobalFilter}
                          searchPlaceholder={searchPlaceholder}
                        />
                      )}

                      {onAPISearch && (
                        <TableAPIFilter
                          onChange={onAPISearch}
                          searchPlaceholder={searchPlaceholder}
                          defaultValue={defaultSearchValue}
                        />
                      )}
                      {!searchable && !onAPISearch && <div />}
                      {filterOptions && (
                        <DropdownList
                          options={filterOptions}
                          fullWidth
                          size="large"
                          height="large"
                          optionClick={handleOptionClick}
                          selected={filterOptions[selected]?.value}
                          alignment="right"
                        >
                          <div className={styles.filterButton}>
                            {filterableIcon && (
                              <FilterIcon size={16} className="text-muted-foreground" />
                            )}
                            <span className="text-muted-foreground">
                              {filterOptions[selected]?.label}
                            </span>
                          </div>
                        </DropdownList>
                      )}
                    </div>
                  </th>
                </tr>
              )}
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, index) => {
                    const headerPropsWithouKey = omit(
                      column.getHeaderProps(column.getSortByToggleProps()),
                      'key'
                    );
                    return (
                      <React.Fragment key={index}>
                        {sortable && column.canSort ? (
                          <th {...headerPropsWithouKey}>
                            <div className={styles.headerItem}>
                              <span>{column.render('Header')}</span>
                              <span>
                                <ArrowUpDownIcon
                                  color={
                                    column.isSorted
                                      ? 'var(--muted-foreground)'
                                      : 'var(--muted-foreground)'
                                  }
                                  size={14}
                                />
                              </span>
                            </div>
                          </th>
                        ) : (
                          <>
                            <th {...column.getHeaderProps()} key={column.getHeaderProps().key}>
                              {column.render('Header')}
                            </th>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </thead>
          )}
          <tbody {...getTableBodyProps()}>
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        // Pass key directly to avoid warning
                        key={cell.getCellProps().key}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className={styles.empty}>{emptyMessage || t('rules.empty')}</div>
        )}
        {loading && (
          <div className={styles.loading}>
            <Spinner size="lg" aria-label={t('common.loading')} />
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(Table);
